/**
 * <AdminProductsTab />
 *
 * Admin moderation over every user's product listings (Phase 24).
 * Closest precedents: AdminDisputesTab (filter bar + flat list of
 * cross-user records) for the overall shape, AdminCategoriesTab for the
 * edit modal + hard/soft delete-confirm modal pattern.
 *
 * Backs onto:
 *   GET    /api/admin/products            -- browse all, any seller/status
 *   PATCH  /api/admin/products/{id}       -- edit any field, force status
 *   DELETE /api/admin/products/{id}       -- hard delete, or archive if
 *                                             the listing has orders
 *
 * The backend filters by exact `seller_id`, not a seller name -- there's
 * no username-substring search endpoint anywhere in this codebase (even
 * AdminUsersTab's "search" is a client-side filter over an already-
 * fetched page, not a server query). So the "Seller" filter here fetches
 * the user list once (GET /admin/users?limit=200, the same endpoint
 * AdminUsersTab uses) and offers a small client-side typeahead that
 * resolves a typed username to the `seller_id` actually sent to the API.
 */
import * as React from "react";
import { AlertCircle, Pencil, Pause, Play, RefreshCcw, Shield, Trash2, X } from "lucide-react";
import { api, extractApiError, parseApiError } from "../../../lib/api";
import type { CategoryNode, Product, ProductCondition, ProductStatus } from "../../../lib/types";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";
import { Select } from "../../../components/ui/Select";
import { PRODUCT_CONDITIONS } from "../../../lib/conditions";
import {
  validateDescription,
  validatePartNumber,
  validatePrice,
  validateStockQuantity,
  validateTitle,
} from "../../../lib/productValidation";

interface AdminProductsPage {
  items: Product[];
  total: number;
  limit: number;
  offset: number;
}

interface SellerLite {
  id: string;
  username: string;
}

interface AdminUsersPage {
  items: SellerLite[];
}

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "SOLD", label: "Sold" },
];

function statusVariant(s: ProductStatus): "brand" | "solid" | "outline" | "soft" {
  if (s === "INACTIVE") return "outline";
  if (s === "SOLD") return "soft";
  return "solid"; // ACTIVE
}

export function AdminProductsTab() {
  const [products, setProducts] = React.useState<Product[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [categories, setCategories] = React.useState<CategoryNode[]>([]);
  const [sellers, setSellers] = React.useState<SellerLite[]>([]);

  // Filters
  const [sellerId, setSellerId] = React.useState("");
  const [sellerLabel, setSellerLabel] = React.useState("");
  const [sellerQuery, setSellerQuery] = React.useState("");
  const [sellerSuggestOpen, setSellerSuggestOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState("");
  const [q, setQ] = React.useState("");
  const [offset, setOffset] = React.useState(0);

  const [editing, setEditing] = React.useState<Product | null>(null);
  const [deleting, setDeleting] = React.useState<Product | null>(null);

  React.useEffect(() => {
    api
      .get<CategoryNode[]>("/categories?tree=true")
      .then((r) => setCategories(r.data))
      .catch(() => {});
    api
      .get<AdminUsersPage>("/admin/users", { params: { limit: 200 } })
      .then((r) => setSellers(r.data.items.map((u) => ({ id: u.id, username: u.username }))))
      .catch(() => {});
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = { limit: PAGE_SIZE, offset };
      if (sellerId) params.seller_id = sellerId;
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category_id = categoryFilter;
      if (q.trim()) params.q = q.trim();
      const r = await api.get<AdminProductsPage>("/admin/products", { params });
      setProducts(r.data.items);
      setTotal(r.data.total);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, [sellerId, statusFilter, categoryFilter, q, offset]);

  React.useEffect(() => {
    load();
  }, [load]);

  const categoriesFlat = React.useMemo(
    () => categories.flatMap((c) => [c, ...c.children]),
    [categories],
  );
  const categoryName = React.useCallback(
    (id?: string | null) => categoriesFlat.find((c) => c.id === id)?.name ?? "—",
    [categoriesFlat],
  );

  const sellerSuggestions = React.useMemo(() => {
    if (!sellerQuery.trim()) return [];
    const needle = sellerQuery.trim().toLowerCase();
    return sellers.filter((s) => s.username.toLowerCase().includes(needle)).slice(0, 8);
  }, [sellers, sellerQuery]);

  const pickSeller = (s: SellerLite) => {
    setSellerId(s.id);
    setSellerLabel(s.username);
    setSellerQuery("");
    setSellerSuggestOpen(false);
    setOffset(0);
  };

  const clearSeller = () => {
    setSellerId("");
    setSellerLabel("");
    setSellerQuery("");
    setOffset(0);
  };

  const replace = (p: Product) => setProducts((prev) => prev.map((x) => (x.id === p.id ? p : x)));

  const togglePause = async (p: Product) => {
    setError(null);
    try {
      const next = p.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const r = await api.patch<Product>(`/admin/products/${p.id}`, { status: next });
      replace(r.data);
    } catch (err) {
      setError(extractApiError(err));
    }
  };

  const onDeleted = (id: string, archived: boolean) => {
    setDeleting(null);
    if (archived) {
      setProducts((prev) =>
        prev.map((x) => (x.id === id ? { ...x, status: "INACTIVE" as ProductStatus } : x)),
      );
    } else {
      setProducts((prev) => prev.filter((x) => x.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    }
  };

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <Card elevated data-testid="admin-products-card">
      <CardHeader>
        <div>
          <h2 className="text-xl font-bold inline-flex items-center gap-2">
            <Shield className="h-5 w-5" aria-hidden /> Products
          </h2>
          <p className="text-ink-muted text-sm mt-1">
            Browse, edit, pause, or remove any listing on the platform — regardless of seller.
            Total: <strong className="text-ink">{total}</strong>
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          loading={loading}
          leadingIcon={<RefreshCcw className="h-4 w-4" />}
          data-testid="admin-products-refresh"
        >
          Refresh
        </Button>
      </CardHeader>
      <CardBody>
        {/* Filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="relative">
            {sellerId ? (
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-ink">Seller</label>
                <div className="flex items-center justify-between h-12 rounded-xl border-2 border-surface-border bg-surface-sunken px-3">
                  <span className="text-sm font-semibold truncate">@{sellerLabel}</span>
                  <button
                    type="button"
                    aria-label="Clear seller filter"
                    onClick={clearSeller}
                    data-testid="admin-products-seller-clear"
                  >
                    <X className="h-4 w-4 text-ink-muted" aria-hidden />
                  </button>
                </div>
              </div>
            ) : (
              <Input
                label="Seller"
                placeholder="Type a username…"
                value={sellerQuery}
                onChange={(e) => {
                  setSellerQuery(e.target.value);
                  setSellerSuggestOpen(true);
                }}
                onFocus={() => setSellerSuggestOpen(true)}
                onBlur={() => setTimeout(() => setSellerSuggestOpen(false), 150)}
                data-testid="admin-products-seller-search"
              />
            )}
            {sellerSuggestOpen && sellerSuggestions.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full rounded-xl border-2 border-surface-border bg-white shadow-lg max-h-48 overflow-auto">
                {sellerSuggestions.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickSeller(s);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-surface-sunken"
                      data-testid={`admin-products-seller-option-${s.id}`}
                    >
                      @{s.username}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setOffset(0);
            }}
            options={STATUS_OPTIONS}
            data-testid="admin-products-status-filter"
          />

          <Select
            label="Category"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setOffset(0);
            }}
            data-testid="admin-products-category-filter"
          >
            <option value="">All categories</option>
            {categories.map((cat) =>
              cat.children.length === 0 ? (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ) : (
                <optgroup key={cat.id} label={cat.name.toUpperCase()}>
                  {cat.children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.name}
                    </option>
                  ))}
                </optgroup>
              ),
            )}
          </Select>

          <Input
            label="Search"
            placeholder="Title or part number"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOffset(0);
            }}
            data-testid="admin-products-q-filter"
          />
        </div>

        {error && (
          <p
            data-testid="admin-products-error"
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand"
          >
            <AlertCircle className="h-4 w-4" /> {error}
          </p>
        )}

        {products.length === 0 ? (
          <p data-testid="admin-products-empty" className="text-ink-muted text-center py-8">
            {loading ? "Loading…" : "No listings match these filters."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-muted text-xs uppercase tracking-wider border-b border-surface-border">
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Seller</th>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3">Price</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
                    <td className="py-3 pr-3 max-w-[220px]">
                      <span className="font-semibold text-ink truncate block">{p.title}</span>
                      <span className="text-xs text-ink-muted font-mono">{p.part_number}</span>
                    </td>
                    <td className="py-3 pr-3">@{p.seller_username ?? "unknown"}</td>
                    <td className="py-3 pr-3">{categoryName(p.category_id)}</td>
                    <td className="py-3 pr-3 font-display font-black">${p.price.toFixed(2)}</td>
                    <td className="py-3 pr-3">
                      <Badge variant={statusVariant(p.status)} size="sm">
                        {p.status}
                      </Badge>
                    </td>
                    <td className="py-3 pr-3 text-ink-muted">
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 pr-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(p)}
                          leadingIcon={<Pencil className="h-4 w-4" />}
                          data-testid={`admin-product-edit-${p.id}`}
                        >
                          Edit
                        </Button>
                        {p.status !== "SOLD" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => togglePause(p)}
                            leadingIcon={
                              p.status === "ACTIVE" ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )
                            }
                            data-testid={`admin-product-toggle-${p.id}`}
                          >
                            {p.status === "ACTIVE" ? "Pause" : "Resume"}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleting(p)}
                          leadingIcon={<Trash2 className="h-4 w-4" />}
                          data-testid={`admin-product-delete-${p.id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-ink-muted" data-testid="admin-products-range">
              Showing {rangeStart}-{rangeEnd} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                data-testid="admin-products-prev"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                data-testid="admin-products-next"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardBody>

      <EditProductModal
        product={editing}
        categories={categories}
        onClose={() => setEditing(null)}
        onSaved={(p) => {
          replace(p);
          setEditing(null);
        }}
      />
      <DeleteProductModal
        product={deleting}
        onClose={() => setDeleting(null)}
        onDone={onDeleted}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Edit modal -- same Phase 23 client-side validation as Sell.tsx / MyListingsTab
// ---------------------------------------------------------------------------
const EDIT_FIELD_ORDER = ["title", "part_number", "price", "description", "stock_quantity"] as const;
type EditField = (typeof EDIT_FIELD_ORDER)[number];

function EditProductModal({
  product,
  categories,
  onClose,
  onSaved,
}: {
  product: Product | null;
  categories: CategoryNode[];
  onClose: () => void;
  onSaved: (p: Product) => void;
}) {
  const [form, setForm] = React.useState({
    title: "",
    description: "",
    part_number: "",
    price: "",
    stock_quantity: "",
    condition: "NEW" as ProductCondition,
    status: "ACTIVE" as ProductStatus,
    category_id: "",
  });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const fieldRefs = React.useRef<Partial<Record<EditField, HTMLElement | null>>>({});

  React.useEffect(() => {
    if (product) {
      setForm({
        title: product.title,
        description: product.description,
        part_number: product.part_number,
        price: String(product.price),
        stock_quantity: String(product.stock_quantity),
        condition: product.condition,
        status: product.status,
        category_id: product.category_id ?? "",
      });
      setFieldErrors({});
      setErr(null);
    }
  }, [product]);

  if (!product) return null;

  const validateOne = (key: EditField): string | undefined => {
    switch (key) {
      case "title":
        return validateTitle(form.title);
      case "part_number":
        return validatePartNumber(form.part_number);
      case "price":
        return validatePrice(form.price);
      case "description":
        return validateDescription(form.description);
      case "stock_quantity":
        return validateStockQuantity(form.stock_quantity);
      default:
        return undefined;
    }
  };

  const handleBlur = (key: EditField) => () => {
    const msg = validateOne(key);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  };

  const focusField = (key: string) => {
    const el = fieldRefs.current[key as EditField];
    el?.focus();
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);

    const errors: Record<string, string> = {};
    for (const key of EDIT_FIELD_ORDER) {
      const msg = validateOne(key);
      if (msg) errors[key] = msg;
    }
    setFieldErrors(errors);
    const firstInvalid = EDIT_FIELD_ORDER.find((k) => errors[k]);
    if (firstInvalid) {
      focusField(firstInvalid);
      return;
    }

    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        title: form.title.trim(),
        description: form.description.trim(),
        part_number: form.part_number.trim(),
        price: Number(form.price),
        stock_quantity: Number(form.stock_quantity),
        condition: form.condition,
        status: form.status,
        category_id: form.category_id || null,
      };
      const r = await api.patch<Product>(`/admin/products/${product.id}`, body);
      onSaved(r.data);
    } catch (e2) {
      const parsed = parseApiError(e2);
      if (Object.keys(parsed.fieldErrors).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...parsed.fieldErrors }));
        const firstServerInvalid = EDIT_FIELD_ORDER.find((k) => parsed.fieldErrors[k]);
        if (firstServerInvalid) focusField(firstServerInvalid);
      } else {
        setErr(parsed.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!product}
      onClose={onClose}
      title="Edit listing"
      description={`Seller: @${product.seller_username ?? "unknown"} — editing as admin.`}
      sizeClass="max-w-2xl"
      testid="admin-product-edit-modal"
    >
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Input
            label="Title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            onBlur={handleBlur("title")}
            error={fieldErrors.title}
            ref={(el) => {
              fieldRefs.current.title = el;
            }}
            data-testid="admin-product-edit-title"
          />
        </div>
        <Input
          label="Part number"
          value={form.part_number}
          onChange={(e) => setForm((f) => ({ ...f, part_number: e.target.value }))}
          onBlur={handleBlur("part_number")}
          error={fieldErrors.part_number}
          ref={(el) => {
            fieldRefs.current.part_number = el;
          }}
          data-testid="admin-product-edit-part-number"
        />
        <Input
          label="Price (USD)"
          type="number"
          step="0.01"
          min="0"
          value={form.price}
          onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
          onBlur={handleBlur("price")}
          error={fieldErrors.price}
          ref={(el) => {
            fieldRefs.current.price = el;
          }}
          data-testid="admin-product-edit-price"
        />
        <Input
          label="Stock"
          type="number"
          min="0"
          value={form.stock_quantity}
          onChange={(e) => setForm((f) => ({ ...f, stock_quantity: e.target.value }))}
          onBlur={handleBlur("stock_quantity")}
          error={fieldErrors.stock_quantity}
          ref={(el) => {
            fieldRefs.current.stock_quantity = el;
          }}
          data-testid="admin-product-edit-stock"
        />
        <Select
          label="Condition"
          value={form.condition}
          onChange={(e) =>
            setForm((f) => ({ ...f, condition: e.target.value as ProductCondition }))
          }
          options={PRODUCT_CONDITIONS.map((c) => ({ value: c.value, label: c.label }))}
          data-testid="admin-product-edit-condition"
        />
        <Select
          label="Status"
          value={form.status}
          onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as ProductStatus }))}
          options={[
            { value: "ACTIVE", label: "Active" },
            { value: "INACTIVE", label: "Inactive" },
            { value: "SOLD", label: "Sold" },
          ]}
          data-testid="admin-product-edit-status"
        />
        <div className="md:col-span-2">
          <Select
            label="Category"
            value={form.category_id}
            onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
            data-testid="admin-product-edit-category"
          >
            <option value="">Uncategorized</option>
            {categories.map((cat) =>
              cat.children.length === 0 ? (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ) : (
                <optgroup key={cat.id} label={cat.name.toUpperCase()}>
                  {cat.children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.name}
                    </option>
                  ))}
                </optgroup>
              ),
            )}
          </Select>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="admin-product-edit-desc" className="mb-1.5 block text-sm font-semibold text-ink">
            Description
          </label>
          <textarea
            id="admin-product-edit-desc"
            data-testid="admin-product-edit-description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            onBlur={handleBlur("description")}
            ref={(el) => {
              fieldRefs.current.description = el;
            }}
            rows={4}
            aria-invalid={Boolean(fieldErrors.description) || undefined}
            className={`w-full rounded-xl border-2 bg-white px-3 py-3 text-base placeholder:text-ink-subtle focus:outline-none ${
              fieldErrors.description
                ? "border-brand focus:border-brand"
                : "border-surface-border focus:border-ink"
            }`}
          />
          {fieldErrors.description && (
            <p
              data-testid="admin-product-edit-description-error"
              className="mt-1.5 text-sm text-brand font-medium"
            >
              {fieldErrors.description}
            </p>
          )}
        </div>

        {err && (
          <p
            className="md:col-span-2 text-sm font-semibold text-brand"
            data-testid="admin-product-edit-error"
          >
            {err}
          </p>
        )}

        <div className="md:col-span-2 flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={busy}
            data-testid="admin-product-edit-submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Delete modal -- hard vs archive, mirroring DeleteCategoryModal
// ---------------------------------------------------------------------------
function DeleteProductModal({
  product,
  onClose,
  onDone,
}: {
  product: Product | null;
  onClose: () => void;
  onDone: (id: string, archived: boolean) => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!product) setErr(null);
  }, [product]);

  if (!product) return null;

  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.delete(`/admin/products/${product.id}`);
      const archived = !!(r.data as { archived?: boolean })?.archived;
      onDone(product.id, archived);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!product}
      onClose={onClose}
      title={`Delete "${product.title}"?`}
      description="If this listing has any orders attached, it will be archived (hidden from the marketplace) instead of removed, so order history stays intact. Otherwise it's gone for good."
      testid="admin-product-delete-modal"
    >
      {err && (
        <p
          className="mb-2 text-sm font-semibold text-brand"
          data-testid="admin-product-delete-error"
        >
          {err}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="md" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          loading={busy}
          onClick={confirm}
          data-testid="admin-product-delete-confirm"
        >
          Delete
        </Button>
      </div>
    </Modal>
  );
}
